// Workout and exercise tracking service
//
// CRITICAL: WHAT MUST *NEVER* HAPPEN AGAIN
// ❌ NO hard-coded "1000 steps" - ONLY use distanceMiles × 2000 for run steps
// ❌ NO multiplying by random constants - ONLY use documented constants (0.04 stepRate, 2000 steps/mile, 0.621371 km→miles)
// ❌ NO baked-in default exercise calories - exerciseCalories = 0 when hasWorkoutEntries = false
// ❌ NO ghost workouts affecting burn or coaching - getDailyBurn() recalculates from scratch, coaching checks validWorkouts.length
// ❌ NO skipping recalculations after delete - loadData() MUST be called after every deletion
// ❌ NO assuming run steps are already removed from totalSteps - ALWAYS calculate: effectiveSteps = max(0, totalSteps - runStepTotal)

import {
  getCollection,
  addDocument,
  updateDocument,
  deleteDocument,
} from './firebase/firestore.service';
import { where, orderBy } from 'firebase/firestore';
import type { WorkoutEntry, DailyBurn } from '../types/workout';
import { detectCategoriesFromWorkout } from '../utils/workoutCategories';
import { getTodayDate, canEditDate, canCreateForDate } from '../utils/date';
import { isDemoMode } from '../demo/demoDetection';
import { triggerFaRecomputeInBackground } from './functionalAge.service';
import { calculateMETWorkoutCalories, elevationExtraKcal } from '../utils/metCalculations';

/**
 * Normalize distance to kilometers.
 * If the description hints at miles (e.g., "mi", "mile", "miles"), treat the numeric distance as miles and convert.
 * Otherwise assume kilometers.
 */
function normalizeDistanceToKm(distance: number, description?: string): number {
  if (!distance || distance <= 0) return distance;
  const desc = (description || '').toLowerCase();
  const mentionsMiles = /\bmi(le)?s?\b/.test(desc);
  return mentionsMiles ? distance * 1.60934 : distance;
}

const WORKOUT_ENTRIES_COLLECTION = 'workoutEntries';

/**
 * Calculate calories burned from steps
 * Rough estimate: ~0.04 kcal per step (varies by person, but this is a reasonable average)
 */
function calculateStepsBurn(effectiveSteps: number): number {
  return Math.round(effectiveSteps * 0.04);
}

/**
 * Calculate steps from run distance using EXACT formula
 * Formula: runSteps = distanceMiles × 2000
 */
function estimateRunStepsFromDistance(distanceKm: number): number {
  const distanceInMiles = distanceKm * 0.621371;
  return Math.round(distanceInMiles * 2000);
}

/**
 * Estimate steps from run duration when distance is missing
 * Approx cadence for steady runs: ~170 steps/min
 */
function estimateRunStepsFromDuration(durationMinutes: number): number {
  return Math.round(durationMinutes * 170);
}

/**
 * Estimate steps from walk distance
 * Fixed rate: 1,250 steps per km (for walks)
 */
function estimateWalkStepsFromDistance(distanceKm: number): number {
  return Math.round(distanceKm * 1250);
}

/**
 * Exported helper: estimate steps from distance for a given activity type.
 * Single source of truth — used by both getDailyBurn and calendar display.
 */
export function estimateStepsFromDistanceKm(distanceKm: number, type: 'run' | 'walk'): number {
  return type === 'run'
    ? estimateRunStepsFromDistance(distanceKm)
    : estimateWalkStepsFromDistance(distanceKm);
}

/**
 * Save a workout entry with offline support
 * If offline, queues entry in IndexedDB for later sync
 * ITEM 4: Demo Mode - blocks all writes
 */
export async function saveWorkoutEntry(
  userId: string,
  entry: Omit<WorkoutEntry, 'id' | 'userId' | 'date' | 'timestamp' | 'createdAt' | 'updatedAt'>,
  date?: string,
): Promise<WorkoutEntry & { pending?: boolean; pendingId?: string }> {
  // ITEM 4: Demo Mode - prevent writes
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('demo') === '1') {
      console.warn('[workout.service] [ITEM 4] BLOCKED: Attempted to save workout entry in demo mode');
      throw new Error('Demo mode: read-only. Cannot save entries.');
    }
  }
  
  const { saveWithOfflineSupport } = await import('./offlineSync.service');
  
  return saveWithOfflineSupport(
    'workout',
    userId,
    entry,
    saveWorkoutEntryDirect,
    date,
  );
}

/**
 * Save a workout entry directly to Firestore (internal use for offline sync)
 */
export async function saveWorkoutEntryDirect(
  userId: string,
  entry: Omit<WorkoutEntry, 'id' | 'userId' | 'date' | 'timestamp' | 'createdAt' | 'updatedAt'>,
  date?: string,
): Promise<WorkoutEntry> {
  const entryDate = date || getTodayDate();
  
  if (!canCreateForDate(entryDate)) {
    throw new Error(`Cannot create entries for ${entryDate}. You can log workouts for today or yesterday only.`);
  }
  
  const timestamp = Date.now();
  const now = Date.now();
  
  // If it's a run, estimate steps from distance using run-specific formula
  if (entry.type === 'run' && entry.distance && entry.distance > 0) {
    const distanceKm = normalizeDistanceToKm(entry.distance, entry.description || entry.pastedText);
    entry.distance = distanceKm;
    entry.stepsFromActivity = estimateRunStepsFromDistance(distanceKm);
  } else if (entry.type === 'run' && entry.duration && entry.duration > 0 && !entry.stepsFromActivity) {
    entry.stepsFromActivity = estimateRunStepsFromDuration(entry.duration);
  }
  
  // If it's a walk, estimate steps from distance using walk formula
  if (entry.type === 'walk' && entry.distance && !entry.stepsFromActivity) {
    const distanceKm = normalizeDistanceToKm(entry.distance, entry.description || entry.pastedText);
    entry.distance = distanceKm;
    entry.stepsFromActivity = estimateWalkStepsFromDistance(distanceKm);
  }
  
  // Detect categories from text (keyword-based)
  const categories = detectCategoriesFromWorkout({
    description: entry.description,
    exercises: entry.exercises,
    pastedText: entry.pastedText,
    type: entry.type,
  });
  
  if (categories.length > 0) {
    entry.categories = categories;
  }
  
  // Remove undefined values (Firestore doesn't accept undefined)
  const cleanedEntry = Object.fromEntries(
    Object.entries(entry).filter(([, value]) => value !== undefined)
  ) as Omit<WorkoutEntry, 'id' | 'userId' | 'date' | 'timestamp' | 'createdAt' | 'updatedAt'>;
  
  const entryData: Omit<WorkoutEntry, 'id'> = {
    ...cleanedEntry,
    type: entry.type,
    userId,
    date: entryDate,
    timestamp,
    createdAt: now,
    updatedAt: now,
  };

  const result = await addDocument<Omit<WorkoutEntry, 'id'>>(
    WORKOUT_ENTRIES_COLLECTION,
    entryData,
  );

  // Fire-and-forget: trigger FA recompute in background after workout save
  triggerFaRecomputeInBackground(userId, 'event:workout');

  return result;
}

/**
 * Get workout entries for a specific date
 */
export async function getWorkoutEntriesForDate(
  userId: string,
  date: string,
): Promise<WorkoutEntry[]> {
  const [firestoreEntries, pendingEntries] = await Promise.all([
    getCollection<WorkoutEntry>(
      WORKOUT_ENTRIES_COLLECTION,
      where('userId', '==', userId),
      where('date', '==', date),
      orderBy('timestamp', 'desc'),
    ),
    // Skip offline queue in demo mode — IndexedDB can hang
    isDemoMode() ? Promise.resolve([]) :
    (async () => {
      try {
        const { getPendingEntriesByType } = await import('./offlineQueue.service');
        const pending = await getPendingEntriesByType(userId, 'workout');
        return pending.filter((p) => p.date === date);
      } catch {
        return [];
      }
    })(),
  ]);

  const pendingAsEntries: WorkoutEntry[] = pendingEntries.map((pending) => ({
    id: pending.id,
    userId: pending.userId,
    date: pending.date,
    timestamp: pending.timestamp,
    createdAt: pending.timestamp,
    updatedAt: pending.timestamp,
    ...(pending.entryData as Omit<WorkoutEntry, 'id' | 'userId' | 'date' | 'timestamp' | 'createdAt' | 'updatedAt' | 'pending'>),
    pending: true,
  }));

  return [...firestoreEntries, ...pendingAsEntries].sort(
    (a, b) => b.timestamp - a.timestamp,
  );
}

/**
 * Get workout entries for a date range
 */
export async function getWorkoutEntriesForDateRange(
  userId: string,
  startDate: string,
  endDate: string,
): Promise<WorkoutEntry[]> {
  const [firestoreEntries, pendingEntries] = await Promise.all([
    (async () => {
      const { getDocumentsByUserAndDateRange } = await import('./firebase/firestore.service');
      return getDocumentsByUserAndDateRange<WorkoutEntry>(
        WORKOUT_ENTRIES_COLLECTION,
        userId,
        startDate,
        endDate,
      );
    })(),
    // Skip offline queue in demo mode — IndexedDB can hang
    isDemoMode() ? Promise.resolve([]) :
    (async () => {
      try {
        const { getPendingEntriesByType } = await import('./offlineQueue.service');
        const pending = await getPendingEntriesByType(userId, 'workout');
        return pending.filter((p) => p.date >= startDate && p.date <= endDate);
      } catch {
        return [];
      }
    })(),
  ]);

  const pendingAsEntries: WorkoutEntry[] = pendingEntries.map((pending) => ({
    id: pending.id,
    userId: pending.userId,
    date: pending.date,
    timestamp: pending.timestamp,
    createdAt: pending.timestamp,
    updatedAt: pending.timestamp,
    ...(pending.entryData as Omit<WorkoutEntry, 'id' | 'userId' | 'date' | 'timestamp' | 'createdAt' | 'updatedAt' | 'pending'>),
    pending: true,
  }));

  return [...firestoreEntries, ...pendingAsEntries].sort(
    (a, b) => b.timestamp - a.timestamp,
  );
}

/**
 * Get daily burn summary (aggregated totals with steps logic)
 * 
 * Key logic:
 * - Walks use MET 3.5 formula: walk_calories = 3.5 * weight_kg * (duration_minutes / 60)
 * - Walk steps estimated as: walk_steps = duration_minutes * 100 (if steps logged)
 * - Net steps = total_steps - run_steps - walk_steps (if both logged)
 * - Runs always use run formula (not MET)
 * - Manual calorie overrides always win
 * - Elevation gain adds extra calories for runs, walks, and cycles
 */
// Single-flight + TTL cache for getDailyBurn
// Prevents duplicate Firestore reads when multiple callers fetch for same uid/date
const burnFetchCache: Map<string, { promise?: Promise<DailyBurn>; result?: DailyBurn; fetchedAt: number }> = new Map();
const BURN_CACHE_TTL_MS = 5000;

export async function getDailyBurn(
  userId: string,
  date: string,
): Promise<DailyBurn> {
  const cacheKey = `${userId}:${date}`;
  const now = Date.now();
  const cached = burnFetchCache.get(cacheKey);

  if (cached?.result && now - cached.fetchedAt < BURN_CACHE_TTL_MS) {
    return cached.result;
  }
  if (cached?.promise) {
    return cached.promise;
  }

  const entry: { promise?: Promise<DailyBurn>; result?: DailyBurn; fetchedAt: number } = { fetchedAt: 0 };
  burnFetchCache.set(cacheKey, entry);

  entry.promise = (async () => {
  const entries = await getWorkoutEntriesForDate(userId, date);
  
  // Get user weight for MET calculations and elevation uplift
  const { getBodyMetrics } = await import('./tdee.service');
  const bodyMetrics = await getBodyMetrics(userId).catch(() => null);
  const userWeightKg = bodyMetrics?.currentWeight || 0;
  
  let totalSteps = 0;
  let runBurn = 0;
  let walkBurn = 0;
  let cycleBurn = 0;
  let weightliftingBurn = 0;
  let manualBurn = 0;
  let runStepTotal = 0;
  let walkStepTotal = 0;
  
  entries.forEach((entry) => {
    if (entry.type === 'steps') {
      totalSteps += entry.steps || 0;
    } else if (entry.type === 'run') {
      // Runs: use stored caloriesBurned (from run formula or manual override)
      const baseRunCal = entry.caloriesBurned;
      runBurn += baseRunCal;
      
      // Elevation uplift for runs
      if (entry.elevationGain && entry.elevationGain > 0 && userWeightKg > 0) {
        runBurn += elevationExtraKcal('run', userWeightKg, entry.elevationGain, baseRunCal);
      }
      
      // Calculate run steps from distance
      if (entry.distance && entry.distance > 0) {
        const distanceKm = normalizeDistanceToKm(entry.distance, entry.description || entry.pastedText);
        runStepTotal += estimateRunStepsFromDistance(distanceKm);
      } else if (entry.stepsFromActivity) {
        runStepTotal += entry.stepsFromActivity;
      } else if (entry.duration && entry.duration > 0) {
        runStepTotal += estimateRunStepsFromDuration(entry.duration);
      }
    } else if (entry.type === 'walk') {
      // Walks: Use MET 3.5 formula or manual override
      if (entry.caloriesBurned > 0) {
        const baseWalkCal = entry.caloriesBurned;
        walkBurn += baseWalkCal;
        
        // Elevation uplift for walks (manual override base)
        if (entry.elevationGain && entry.elevationGain > 0 && userWeightKg > 0) {
          walkBurn += elevationExtraKcal('walk', userWeightKg, entry.elevationGain, baseWalkCal);
        }
      } else if (entry.duration && entry.duration > 0 && userWeightKg > 0) {
        const baseWalkCal = calculateMETWorkoutCalories('walking', userWeightKg, entry.duration);
        walkBurn += baseWalkCal;
        
        // Elevation uplift for walks (MET-calculated base)
        if (entry.elevationGain && entry.elevationGain > 0) {
          walkBurn += elevationExtraKcal('walk', userWeightKg, entry.elevationGain, baseWalkCal);
        }
      }
      
      // Estimate walk steps
      if (entry.distance && entry.distance > 0) {
        const distanceKm = normalizeDistanceToKm(entry.distance, entry.description || entry.pastedText);
        walkStepTotal += estimateWalkStepsFromDistance(distanceKm);
      } else if (entry.stepsFromActivity) {
        walkStepTotal += entry.stepsFromActivity;
      } else if (entry.duration && entry.duration > 0) {
        walkStepTotal += Math.round(entry.duration * 100);
      }
    } else if (entry.type === 'cycle') {
      // Cycling: use stored caloriesBurned (from speed-aware MET or manual override)
      const baseCycleCal = entry.caloriesBurned;
      cycleBurn += baseCycleCal;
      
      // Elevation uplift for cycling
      if (entry.elevationGain && entry.elevationGain > 0 && userWeightKg > 0) {
        cycleBurn += elevationExtraKcal('cycle', userWeightKg, entry.elevationGain, baseCycleCal);
      }
    } else if (entry.type === 'weightlifting') {
      weightliftingBurn += entry.caloriesBurned;
    } else if (entry.type === 'manual') {
      manualBurn += entry.caloriesBurned;
    }
  });
  
  // Calculate step calories based on movement steps exclusion
  let stepsBurn = 0;
  let effectiveSteps = 0;
  
  if (totalSteps > 0) {
    const movementSteps = runStepTotal + walkStepTotal;
    effectiveSteps = Math.max(0, totalSteps - movementSteps);
    stepsBurn = calculateStepsBurn(effectiveSteps);
  }
  
  const totalDailyBurn = stepsBurn + runBurn + walkBurn + cycleBurn + weightliftingBurn + manualBurn;
  const runsWalksBurn = runBurn + walkBurn + cycleBurn;
  const stepsFromRunsWalks = runStepTotal + walkStepTotal;
  const displayedSteps = totalSteps;
  
    const result: DailyBurn = {
      date,
      userId,
      totalSteps,
      stepsFromRunsWalks,
      effectiveSteps,
      displayedSteps,
      stepsBurn,
      runsWalksBurn,
      weightliftingBurn,
      manualBurn,
      totalDailyBurn,
      entries,
    };
    entry.result = result;
    entry.fetchedAt = Date.now();
    entry.promise = undefined;
    return result;
  })();

  return entry.promise;
}

/**
 * Update a workout entry
 */
export async function updateWorkoutEntry(
  entryId: string,
  updates: Partial<WorkoutEntry>,
): Promise<WorkoutEntry> {
  const { getDocument } = await import('./firebase/firestore.service');
  const current = await getDocument<WorkoutEntry>(`${WORKOUT_ENTRIES_COLLECTION}/${entryId}`);
  if (current && !canEditDate(current.date)) {
    throw new Error(`Cannot edit entries for ${current.date}. Days auto-close at 23:59. Only today can be edited (or yesterday if QA mode is enabled).`);
  }
  
  const contentChanged = 
    updates.description !== undefined || 
    updates.exercises !== undefined || 
    updates.pastedText !== undefined || 
    updates.type !== undefined;
  
  if (contentChanged && current) {
    const merged = { ...current, ...updates };
    const categories = detectCategoriesFromWorkout({
      description: merged.description,
      exercises: merged.exercises,
      pastedText: merged.pastedText,
      type: merged.type,
    });
    
    if (categories.length > 0) {
      updates.categories = categories;
    }
    
    updates.coachFeedback = undefined;
  }
  
  await updateDocument(`${WORKOUT_ENTRIES_COLLECTION}/${entryId}`, {
    ...updates,
    updatedAt: Date.now(),
  });
  
  const updated = await getDocument<WorkoutEntry>(`${WORKOUT_ENTRIES_COLLECTION}/${entryId}`);
  if (!updated) {
    throw new Error('Failed to retrieve updated workout');
  }
  return updated;
}

/**
 * Update a workout entry and regenerate coach feedback if content changed
 */
export async function updateWorkoutEntryAndRegenerateFeedback(
  entryId: string,
  updates: Partial<WorkoutEntry>,
  _userId: string,
  generateFeedback: (workout: WorkoutEntry) => Promise<void>,
): Promise<WorkoutEntry> {
  const contentChanged = 
    updates.description !== undefined || 
    updates.exercises !== undefined || 
    updates.pastedText !== undefined || 
    updates.type !== undefined;
  
  const updated = await updateWorkoutEntry(entryId, updates);
  
  if (contentChanged && updated.id) {
    generateFeedback(updated).catch((error) => {
      console.error('Failed to regenerate coach feedback after workout update:', error);
    });
  }
  
  return updated;
}

/**
 * Delete a workout entry
 */
export async function deleteWorkoutEntry(entryId: string): Promise<void> {
  const { isDemoMode: checkDemoMode } = await import('../demo/demoDetection');
  if (checkDemoMode()) {
    throw new Error('Demo mode: read-only. Cannot delete entries.');
  }
  
  const { getDocument } = await import('./firebase/firestore.service');
  const existing = await getDocument<WorkoutEntry>(`${WORKOUT_ENTRIES_COLLECTION}/${entryId}`);
  if (existing && !canEditDate(existing.date)) {
    throw new Error(`Cannot edit entries for ${existing.date}. Days auto-close at 23:59. Only today can be edited (or yesterday if QA mode is enabled).`);
  }
  
  await deleteDocument(`${WORKOUT_ENTRIES_COLLECTION}/${entryId}`);
}

/** Invalidate the getDailyBurn cache for a specific uid/date (call after saves/deletes). */
export function invalidateBurnCache(userId: string, date: string): void {
  burnFetchCache.delete(`${userId}:${date}`);
}
